// src/components/DepartmentChart.jsx
import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList,
} from "recharts";

const getLabel = (value, chartType) => {
  if (chartType === "stress") {
    if (value <= 2) return "Low";
    if (value <= 4) return "Mild";
    if (value <= 6) return "Moderate";
    if (value <= 8) return "High";
    return "Severe";
  }

  if (chartType === "wrmsd") {
    if (value === 0) return "No Pain";
    if (value <= 2) return "Mild";
    if (value <= 5) return "Moderate To Severe";
    if (value <= 8) return "Very Severe";
    return "Worst Pain Possible";
  }

  // default (load/productivity)
  if (value <= 5) return "Low";
  if (value <= 10) return "Regular";
  if (value <= 15) return "Moderate";
  return "High";
};

const getGradientColor = (label, chartType) => {
  if (chartType === "stress" || chartType === "wrmsd") {
    switch (label) {
      case "No Pain":
      case "Low":
        return "#e0e0ff";
      case "Mild":
        return "#c0c0ff";
      case "Moderate":
      case "Moderate To Severe":
        return "#a0a0ff";
      case "High":
      case "Very Severe":
        return "#8080ff";
      case "Severe":
      case "Worst Pain Possible":
        return "#eeb577ff";
      default:
        return "#dcdcdc";
    }
  }
  // default (load/productivity)
  switch (label) {
    case "Low":
      return "#e0e0ff";
    case "Regular":
      return "#c0c0ff";
    case "Moderate":
      return "#a0a0ff";
    case "High":
      return "#8080ff";
    default:
      return "#dcdcdc";
  }
};

// label to the right of each bar
const CustomLabel = (props) => {
  const { x, y, width, height, value, payload } = props;
  const category = payload?.categoryLabel || getLabel(value, payload?.chartType);
  return (
    <text
      x={x + width + 5}
      y={y + height / 2}
      fill="#333"
      fontSize="11"
      fontWeight="500"
      textAnchor="start"
      dominantBaseline="middle"
    >
      {`${value} (${category})`}
    </text>
  );
};

const DepartmentChart = ({
  data = [],
  title = "Department Metric",
  number = 10,
  chartType = "default",
}) => {
  // Normalize one source of truth for the category label
  const processedData = data.map((item) => {
    const categoryLabel = getLabel(item.value, chartType);
    return {
      ...item,
      categoryLabel,
      chartType,
    };
  });

  const getLegendItems = () => {
    if (chartType === "stress") {
      return [
        { label: "Low", color: "#e0e0ff", range: "1-2" },
        { label: "Mild", color: "#c0c0ff", range: "3-4" },
        { label: "Moderate", color: "#a0a0ff", range: "5-6" },
        { label: "High", color: "#8080ff", range: "7-8" },
        { label: "Severe", color: "#eeb577ff", range: "9-10" },
      ];
    }
    if (chartType === "wrmsd") {
      return [
        { label: "No Pain", color: "#e0e0ff", range: "0" },
        { label: "Mild", color: "#c0c0ff", range: "1-2" },
        { label: "Moderate To Severe", color: "#a0a0ff", range: "3-5" },
        { label: "Very Severe", color: "#8080ff", range: "6-8" },
        { label: "Worst Pain Possible", color: "#eeb577ff", range: "9-10" },
      ];
    }
    return [
      { label: "Low", color: "#e0e0ff", range: "≤ 5" },
      { label: "Regular", color: "#c0c0ff", range: "6-10" },
      { label: "Moderate", color: "#a0a0ff", range: "11-15" },
      { label: "High", color: "#8080ff", range: "> 15" },
    ];
  };

  const legendItems = getLegendItems();

  return (
    <>
      <p className="fw-semibold">{title}</p>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart
          layout="vertical"
          data={processedData}
          margin={{ top: 10, right: 80, left: 40, bottom: 10 }}
        >
          <XAxis type="number" domain={[0, number]} tick={{ fontSize: 12 }} />
          <YAxis
            type="category"
            dataKey="departmentName"
            tick={{ fontSize: 12 }}
            width={100}
          />
          <Tooltip
            formatter={(value, _name, props) => [
              `${value} (${props.payload.categoryLabel})`,
              "Value",
            ]}
            labelFormatter={(name) => `Department: ${name}`}
          />
          <Bar dataKey="value" radius={[0, 10, 10, 0]}>
            {processedData.map((entry, i) => (
              <Cell
                key={`cell-${i}`}
                fill={getGradientColor(entry.categoryLabel, chartType)}
              />
            ))}
            <LabelList content={<CustomLabel />} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className="mt-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <small className="text-muted fw-semibold">Legend:</small>
        </div>
        <div className="d-flex justify-content-center flex-wrap gap-2">
          {legendItems.map((item, idx) => (
            <div key={idx} className="d-flex align-items-center" style={{ fontSize: 12 }}>
              <div
                className="me-2"
                style={{
                  width: 16,
                  height: 16,
                  backgroundColor: item.color,
                  borderRadius: 3,
                  border: "1px solid #ddd",
                  flexShrink: 0,
                }}
              />
              <span className="text-dark"><strong>{item.label}</strong></span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default DepartmentChart;
